import { useRef, useState, useCallback, useEffect } from 'react'
import { getPassengerSuggestions, type PassengerSuggestion } from '@/api/orders'
import { InputWithClear } from '@/components/ui/input-with-clear'

interface PassengerAutocompleteProps {
  value: string
  onChange: (value: string) => void
  onSelect: (suggestion: PassengerSuggestion) => void
  placeholder?: string
  className?: string
  errorClassName?: string
  hasError?: boolean
  required?: boolean
}

export default function PassengerAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder,
  className,
  hasError,
  required,
}: PassengerAutocompleteProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [suggestions, setSuggestions] = useState<PassengerSuggestion[]>([])
  const containerRef = useRef<HTMLDivElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const fetchSuggestions = useCallback((q: string) => {
    getPassengerSuggestions(q)
      .then(res => setSuggestions(res || []))
      .catch(() => setSuggestions([]))
  }, [])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value)
    setIsOpen(true)
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => fetchSuggestions(e.target.value), 300)
  }

  const handleFocus = () => {
    setIsOpen(true)
    fetchSuggestions(value)
  }

  const handleSelect = (s: PassengerSuggestion) => {
    onSelect(s)
    setIsOpen(false)
  }

  return (
    <div ref={containerRef} className="relative">
      <InputWithClear
        placeholder={placeholder}
        className={`${className || ''} ${hasError ? 'border-red-400' : ''}`}
        value={value}
        onChange={handleChange}
        onFocus={handleFocus}
        onClear={() => onChange('')}
        required={required}
      />
      {isOpen && suggestions.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg z-[100] max-h-48 overflow-y-auto">
          {suggestions.map((s, idx) => (
            <button
              key={`${s.passenger_name}-${s.passenger_phone}-${idx}`}
              type="button"
              onClick={() => handleSelect(s)}
              className="w-full text-left px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer"
            >
              <div className="text-sm font-medium text-gray-800 dark:text-gray-100">{s.passenger_name}</div>
              {s.passenger_phone && <div className="text-xs text-gray-500 dark:text-gray-400">{s.passenger_phone}</div>}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
